import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '@/hooks/useDashboard';
import { useAppointments } from '@/hooks/useAppointments';
import { useCrm } from '@/hooks/useCrm';
import { useSettingsStore, useAppointmentsStore, useCrmStore } from '@/stores';
import { StatCard } from '@/components/cards/StatCard';
import { OperationalAlerts } from '@/components/dashboard/OperationalAlerts';
import { CardSkeleton } from '@/components/common/SkeletonLoader';
import { fetchLatestRate, loadRatesCache } from '@/services/rates.service';
import { formatCurrency } from '@/utils/currency';
import { matchesCrmSubFilter } from '@/utils/crmHelpers';
import {
  Calendar,
  DollarSign,
  Euro,
  RefreshCw,
  Scissors,
  Users,
  Bell,
  ArrowRight,
  TrendingUp,
  Megaphone,
  SlidersHorizontal,
} from 'lucide-react';

export function DashboardPage() {
  const navigate = useNavigate();
  const { data, loading: loadingDashboard, error, refresh: refreshDashboard } = useDashboard();
  const { items: appointments, loading: loadingAppointments, refresh: refreshAppointments } = useAppointments();
  const { clients, segmentMemberships, segments, loading: loadingCrm, fetchSegmentCustomers } = useCrm();
  const settings = useSettingsStore();

  const [apiRate, setApiRate] = useState<number>(() => {
    const cached = loadRatesCache()?.latest?.[settings.currencyCode]?.rate;
    return cached || 0;
  });

  // Fetch live exchange rate for dual currency calculation in auto mode
  useEffect(() => {
    if (settings.currencyDisplayMode === 'manual') return;

    let isMounted = true;
    fetchLatestRate(settings.currencyCode || 'EUR')
      .then((rec) => {
        if (isMounted && rec?.rate) {
          setApiRate(rec.rate);
        }
      })
      .catch(() => {
        // Fallback to cache or store
      });

    return () => {
      isMounted = false;
    };
  }, [settings.currencyCode, settings.currencyDisplayMode]);

  // Derive effective rate with recargo
  const baseRate =
    settings.currencyDisplayMode === 'manual' && settings.manualRate > 0
      ? settings.manualRate
      : apiRate || 0;

  const effectiveRate = baseRate > 0 ? baseRate * (1 + (settings.recargo || 0) / 100) : 0;

  // Extract metrics safely
  const todayBookingsCount =
    data?.todaysBookings?.meta?.bookings_count ??
    data?.todaysBookings?.data?.attributes?.['bookings-count'] ??
    appointments.length;

  const recentSalesAmount =
    data?.recentSales?.data?.attributes?.totals?.sales_value ??
    data?.recentSales?.data?.attributes?.['total-sales'] ??
    0;

  // Dual-currency calculation (EUR / USD -> Bs.)
  const bsAmount =
    effectiveRate > 0 && recentSalesAmount > 0
      ? (recentSalesAmount * effectiveRate).toLocaleString('es-VE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : null;

  // Operational alerts metrics
  const unconfirmedCount = useMemo(() => {
    return appointments.filter((a) => a.status === 'unconfirmed' || a.status === 'new').length;
  }, [appointments]);

  const birthdayCount = useMemo(() => {
    return clients.filter((c) =>
      matchesCrmSubFilter(c, 'birthday', settings, segmentMemberships, segments)
    ).length;
  }, [clients, settings, segmentMemberships, segments]);

  const debtorCount = useMemo(() => {
    const minDebt = settings.debtorMinAmount || 0.01;
    return clients.filter((c) => (c.outstandingAmount || 0) >= minDebt).length;
  }, [clients, settings.debtorMinAmount]);

  const totalAlerts = unconfirmedCount + birthdayCount + debtorCount;

  // Top services table
  const topServicesTable = data?.topServices?.included?.find(
    (inc: any) => inc.type === 'report-tables'
  );
  const topRows = topServicesTable?.attributes?.rows || [];

  const topServicesList: Array<{ name: string; count: number }> = topRows.map((r: any) => ({
    name: r.cells?.name?.value || 'Servicio',
    count: r.cells?.last_month?.value ?? r.cells?.this_month?.value ?? 0,
  }));

  const maxServiceCount = Math.max(...topServicesList.map((s) => s.count), 1);

  const isEur = settings.currencyCode === 'EUR';

  // Navigation handlers
  const handleUnconfirmedClick = () => {
    useAppointmentsStore.getState().setFilterStatus('unconfirmed');
    navigate('/');
  };

  const handleBirthdayClick = () => {
    useCrmStore.getState().setActiveSegmentId('');
    useCrmStore.getState().setActiveSubFilter('birthday');
    navigate('/crm');
  };

  const handleDebtorClick = () => {
    useCrmStore.getState().setActiveSegmentId('');
    useCrmStore.getState().setActiveSubFilter('debt');
    navigate('/crm');
  };

  const handleRefreshAll = () => {
    refreshDashboard();
    refreshAppointments();
    fetchSegmentCustomers();
  };

  const isAnyLoading = loadingDashboard || loadingAppointments || loadingCrm;

  return (
    <div className="max-w-6xl mx-auto w-full space-y-6 py-4 px-4 sm:px-6">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-text-primary tracking-tight">Panel de Control</h2>
          <p className="text-xs text-text-muted">Resumen ejecutivo del negocio y acciones operativas</p>
        </div>
        <button
          onClick={handleRefreshAll}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-surface hover:bg-surface-secondary text-text-secondary text-xs font-semibold cursor-pointer shadow-xs transition-colors"
          title="Recargar métricas"
        >
          <RefreshCw size={14} className={isAnyLoading ? 'animate-spin text-brand-primary' : ''} />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Error state if dashboard query fails */}
      {error && (
        <div className="p-4 bg-brand-danger/10 border border-brand-danger/20 rounded-xl text-center">
          <p className="text-sm text-brand-danger font-medium">{error}</p>
          <button
            onClick={handleRefreshAll}
            className="mt-2 text-xs px-3 py-1.5 bg-brand-danger text-white rounded-lg cursor-pointer"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* 1. Operational Alerts Banner / Widget */}
      <OperationalAlerts
        unconfirmedCount={unconfirmedCount}
        birthdayCount={birthdayCount}
        debtorCount={debtorCount}
        loading={isAnyLoading}
        onUnconfirmedClick={handleUnconfirmedClick}
        onBirthdayClick={handleBirthdayClick}
        onDebtorClick={handleDebtorClick}
      />

      {/* 2. Interactive Key Metrics Cards (2 cols mobile, 4 cols desktop) */}
      {loadingDashboard && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Citas de Hoy */}
          <StatCard
            title="Citas de Hoy"
            value={todayBookingsCount}
            subtitle="Abrir agenda de hoy"
            icon={<Calendar size={18} />}
            onClick={() => navigate('/')}
            className="hover:border-brand-primary/40"
          />

          {/* Ventas Recientes con Dual Currency */}
          <StatCard
            title="Ventas Recientes"
            value={formatCurrency(recentSalesAmount, settings.currencyCode)}
            bsValue={bsAmount ? `Bs. ${bsAmount}` : undefined}
            subtitle={
              effectiveRate > 0
                ? `1 ${settings.currencyCode} ≈ Bs. ${effectiveRate.toFixed(2)}`
                : 'Últimos registros'
            }
            icon={isEur ? <Euro size={18} /> : <DollarSign size={18} />}
            onClick={() => navigate('/crm')}
            className="hover:border-brand-primary/40"
          />

          {/* Clientes / Actividad CRM */}
          <StatCard
            title="Clientes en CRM"
            value={clients.length > 0 ? clients.length : (data?.bookingsActivity?.meta?.clients_count ?? '—')}
            subtitle="Fidelización y segmentación"
            icon={<Users size={18} />}
            onClick={() => navigate('/crm')}
            className="hover:border-brand-primary/40"
          />

          {/* Alertas Operativas */}
          <StatCard
            title="Alertas del Día"
            value={totalAlerts}
            subtitle={totalAlerts > 0 ? 'Acciones prioritarias' : 'Todo al día'}
            icon={<Bell size={18} />}
            trend={
              totalAlerts > 0
                ? { value: `${totalAlerts} pendientes`, isPositive: false }
                : { value: 'OK', isPositive: true }
            }
            onClick={() => {
              if (unconfirmedCount > 0) handleUnconfirmedClick();
              else if (birthdayCount > 0) handleBirthdayClick();
              else if (debtorCount > 0) handleDebtorClick();
              else navigate('/');
            }}
            className="hover:border-brand-primary/40"
          />
        </div>
      )}

      {/* 3. Bottom Grid: Top Services & Quick Launchpad */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Top Services Card */}
        <div className="bg-surface border border-border rounded-xl p-4 sm:p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-brand-primary/10 text-brand-primary">
                <Scissors size={16} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-text-primary">Servicios Más Solicitados</h3>
                <p className="text-xs text-text-muted">Desempeño mensual de servicios</p>
              </div>
            </div>
            <TrendingUp size={16} className="text-brand-success" />
          </div>

          {topServicesList.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-xs text-text-muted">Sin datos de servicios disponibles</p>
            </div>
          ) : (
            <div className="space-y-3 pt-1">
              {topServicesList.slice(0, 5).map((srv, idx) => {
                const percentage = Math.round((srv.count / maxServiceCount) * 100);
                return (
                  <div key={idx} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-medium">
                      <div className="flex items-center gap-2 truncate">
                        <span className="w-5 h-5 rounded-full bg-surface-secondary text-text-muted text-[10px] flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-text-primary truncate">{srv.name}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-surface-secondary text-text-primary font-bold rounded-md">
                        {srv.count} {srv.count === 1 ? 'cita' : 'citas'}
                      </span>
                    </div>
                    {/* Visual Progress Bar */}
                    <div className="w-full bg-surface-secondary rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-brand-primary h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Launchpad / Shortcuts */}
        <div className="bg-surface border border-border rounded-xl p-4 sm:p-5 shadow-xs space-y-4">
          <div>
            <h3 className="text-sm font-bold text-text-primary">Accesos Directos de Gestión</h3>
            <p className="text-xs text-text-muted">Navegación rápida a los módulos clave</p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex flex-col items-start p-3 rounded-xl border border-border bg-surface-secondary/50 hover:bg-surface-secondary hover:border-brand-primary/40 transition-all text-left group cursor-pointer"
            >
              <div className="p-2 rounded-lg bg-brand-primary/10 text-brand-primary mb-2 group-hover:bg-brand-primary group-hover:text-white transition-colors">
                <Calendar size={18} />
              </div>
              <span className="text-xs font-bold text-text-primary">Agenda Diaria</span>
              <span className="text-[11px] text-text-muted mt-0.5">Control de citas y WhatsApp</span>
              <div className="flex items-center gap-1 text-[10px] font-semibold text-brand-primary mt-2 group-hover:translate-x-0.5 transition-transform">
                <span>Ir a Agenda</span>
                <ArrowRight size={10} />
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/crm')}
              className="flex flex-col items-start p-3 rounded-xl border border-border bg-surface-secondary/50 hover:bg-surface-secondary hover:border-brand-primary/40 transition-all text-left group cursor-pointer"
            >
              <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 mb-2 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                <Users size={18} />
              </div>
              <span className="text-xs font-bold text-text-primary">Clientes CRM</span>
              <span className="text-[11px] text-text-muted mt-0.5">Segmentos, deudas y fidelización</span>
              <div className="flex items-center gap-1 text-[10px] font-semibold text-purple-600 dark:text-purple-400 mt-2 group-hover:translate-x-0.5 transition-transform">
                <span>Ver Clientes</span>
                <ArrowRight size={10} />
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/campaigns')}
              className="flex flex-col items-start p-3 rounded-xl border border-border bg-surface-secondary/50 hover:bg-surface-secondary hover:border-brand-primary/40 transition-all text-left group cursor-pointer"
            >
              <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mb-2 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <Megaphone size={18} />
              </div>
              <span className="text-xs font-bold text-text-primary">Campañas</span>
              <span className="text-[11px] text-text-muted mt-0.5">Reactivación de clientes ausentes</span>
              <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 mt-2 group-hover:translate-x-0.5 transition-transform">
                <span>Ver Campañas</span>
                <ArrowRight size={10} />
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="flex flex-col items-start p-3 rounded-xl border border-border bg-surface-secondary/50 hover:bg-surface-secondary hover:border-brand-primary/40 transition-all text-left group cursor-pointer"
            >
              <div className="p-2 rounded-lg bg-slate-500/10 text-slate-600 dark:text-slate-400 mb-2 group-hover:bg-slate-700 group-hover:text-white transition-colors">
                <SlidersHorizontal size={18} />
              </div>
              <span className="text-xs font-bold text-text-primary">Configuración</span>
              <span className="text-[11px] text-text-muted mt-0.5">Tasas, mensajes y conexión en la nube</span>
              <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 dark:text-slate-400 mt-2 group-hover:translate-x-0.5 transition-transform">
                <span>Ajustes</span>
                <ArrowRight size={10} />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
